import type {
  BusinessReach,
  NationalOption,
  AudienceType,
  PhysicalPresence,
  AgeRange,
  Gender,
} from "../types";
import { majorCities } from "../data";
import { useRef, useEffect, useState } from "react";
import { apiService } from "../../../services/api";
import FieldInfo from "../../../components/base/FieldInfo";

interface StepEcosystemProps {
  errors: Record<string, string>;
  businessReach: BusinessReach | null;
  nationalOption: NationalOption | null;
  nationalCityInput: string;
  nationalCities: string[];
  showNationalDropdown: boolean;
  localLocations: string[];
  localLocationInput: string;
  showLocalDropdown: boolean;
  audienceType: AudienceType | null;
  personaDescription: string;
  targetAgeRange: AgeRange[];
  targetGender: Gender[];
  interests: string[];
  interestInput: string;
  professions: string[];
  professionInput: string;
  physicalPresence: PhysicalPresence | null;
  competitors: { name: string; website: string; insights: string }[];
  visibleCompetitorCount: number;
  onShowMoreCompetitors: () => void;
  onBusinessReachChange: (value: BusinessReach | null) => void;
  onNationalOptionChange: (value: NationalOption | null) => void;
  onNationalCityInputChange: (value: string) => void;
  onShowNationalDropdown: (value: boolean) => void;
  onAddNationalCity: (city: string) => void;
  onRemoveNationalCity: (index: number) => void;
  onLocalLocationInputChange: (value: string) => void;
  onShowLocalDropdown: (value: boolean) => void;
  onAddLocalLocation: (location: string) => void;
  onRemoveLocalLocation: (index: number) => void;
  onAudienceTypeChange: (value: AudienceType | null) => void;
  onPersonaDescriptionChange: (value: string) => void;
  onTargetAgeRangeChange: (value: AgeRange[]) => void;
  onTargetGenderChange: (value: Gender[]) => void;
  onInterestInputChange: (value: string) => void;
  onAddInterest: (interest: string) => void;
  onRemoveInterest: (index: number) => void;
  onProfessionInputChange: (value: string) => void;
  onAddProfession: (profession: string) => void;
  onRemoveProfession: (index: number) => void;
  onPhysicalPresenceChange: (value: PhysicalPresence | null) => void;
  onUpdateCompetitor: (
    index: number,
    field: "name" | "website" | "insights",
    value: string,
  ) => void;
  onRemoveCompetitor: (index: number) => void;
}

export default function StepEcosystem(props: StepEcosystemProps) {
  const localInputRef = useRef<HTMLInputElement | null>(null);
  const nationalInputRef = useRef<HTMLInputElement | null>(null);

  const [localSuggestions, setLocalSuggestions] = useState<Array<{ description: string, place_id: string }>>([]);
  const [nationalSuggestions, setNationalSuggestions] = useState<Array<{ description: string, place_id: string }>>([]);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);

  useEffect(() => {
    const fetchNationalSuggestions = async () => {
      if (!props.nationalCityInput.trim()) {
        setNationalSuggestions([]);
        return;
      }
      try {
        setSuggestionError(null);
        const res: any = await apiService.getAddressAutocomplete(props.nationalCityInput);
        const preds = res?.predictions || res?.data?.predictions || [];
        // Filter out highly specific addresses (streets, neighborhoods) which typically have > 3 parts
        const citySuggestions = preds.filter((p: any) => p.description.split(',').length <= 3);
        setNationalSuggestions(citySuggestions);
      } catch (err: any) {
        console.error('Failed to fetch national address suggestions', err);
        setSuggestionError(err?.message || 'Failed to fetch');
      }
    };

    const timer = setTimeout(() => {
      fetchNationalSuggestions();
    }, 300);

    return () => clearTimeout(timer);
  }, [props.nationalCityInput]);

  useEffect(() => {
    const fetchLocalSuggestions = async () => {
      if (!props.localLocationInput.trim()) {
        setLocalSuggestions([]);
        return;
      }
      try {
        setSuggestionError(null);
        const res: any = await apiService.getAddressAutocomplete(props.localLocationInput);
        const preds = res?.predictions || res?.data?.predictions || [];
        setLocalSuggestions(preds);
      } catch (err: any) {
        console.error('Failed to fetch local address suggestions', err);
        setSuggestionError(err?.message || 'Failed to fetch');
      }
    };

    const timer = setTimeout(() => {
      fetchLocalSuggestions();
    }, 300);

    return () => clearTimeout(timer);
  }, [props.localLocationInput]);

  const isPanIndiaSelected =
    props.businessReach === "national" && props.nationalOption === "panIndia";
  const isCityOrCountrySelected =
    props.businessReach === "national" &&
    props.nationalOption === "cityOrCountry";

  const handleAgeToggle = (ageId: AgeRange) => {
    if (ageId === "all-ages") {
      props.onTargetAgeRangeChange(["all-ages"]);
    } else {
      let newAges = [...(props.targetAgeRange || [])];
      if (newAges.includes("all-ages")) {
        newAges = [];
      }
      if (newAges.includes(ageId)) {
        newAges = newAges.filter((a) => a !== ageId);
      } else {
        newAges.push(ageId);
      }
      props.onTargetAgeRangeChange(newAges);
    }
  };

  const handleGenderToggle = (genderId: Gender) => {
    if (genderId === "all") {
      props.onTargetGenderChange(["all"]);
    } else {
      let newGenders = [...(props.targetGender || [])];
      if (newGenders.includes("all")) {
        newGenders = [];
      }
      if (newGenders.includes(genderId)) {
        newGenders = newGenders.filter((g) => g !== genderId);
      } else {
        newGenders.push(genderId);
      }
      props.onTargetGenderChange(newGenders);
    }
  };

  return (
    <div className="space-y-6">
      {/* Section Header */}
      <div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Ecosystem</h2>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Business Reach & Location Block */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-global-line text-teal-600 text-sm"></i>
          </div>
          Business Reach & Location
        </h3>

        <div className="space-y-5">
          {/* Location Details Summary */}
          {((props.businessReach === "local" && props.localLocations.length > 0) ||
            (props.businessReach === "national" &&
              (props.nationalOption === "panIndia" ||
                props.nationalCities.length > 0)) ||
            props.businessReach === "global") && (
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Current Location Details
                </h4>
                <div className="space-y-2">
                  {props.businessReach === "local" && props.localLocations.length > 0 && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="w-6 h-6 rounded bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-map-pin-2-line text-teal-600 text-xs"></i>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700 block mb-1">
                          Local — {props.localLocations.length} area(s)
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {props.localLocations.map((loc, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-2 py-0.5 bg-teal-50 border border-teal-100 rounded text-xs text-teal-700"
                            >
                              {loc}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {props.businessReach === "national" && props.nationalOption === "panIndia" && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="w-6 h-6 rounded bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-flag-line text-teal-600 text-xs"></i>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700 block">
                          National — Pan India
                        </span>
                        <span className="text-xs text-gray-500">
                          Coverage applies to all cities country-wide.
                        </span>
                      </div>
                    </div>
                  )}

                  {props.businessReach === "national" && props.nationalOption === "cityOrCountry" && props.nationalCities.length > 0 && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="w-6 h-6 rounded bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-building-line text-teal-600 text-xs"></i>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700 block mb-1">
                          National — {props.nationalCities.length} city/country
                        </span>
                        <div className="flex flex-wrap gap-1.5">
                          {props.nationalCities.map((city, i) => (
                            <span
                              key={i}
                              className="inline-flex items-center px-2 py-0.5 bg-teal-50 border border-teal-100 rounded text-xs text-teal-700"
                            >
                              {city}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {props.businessReach === "global" && (
                    <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg border border-gray-100">
                      <div className="w-6 h-6 rounded bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-earth-line text-teal-600 text-xs"></i>
                      </div>
                      <div className="flex-1">
                        <span className="text-sm font-medium text-gray-700 block">
                          Global
                        </span>
                        <span className="text-xs text-gray-500">
                          International reach selected.
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

          {/* Business Reach Type */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <span>Business Reach Type</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="businessReach" />
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  id: "local",
                  label: "Local",
                  icon: "ri-map-pin-2-line",
                  desc: "Area-specific",
                },
                {
                  id: "national",
                  label: "National",
                  icon: "ri-map-2-line",
                  desc: "City/Country-wide coverage",
                },
                {
                  id: "global",
                  label: "Global",
                  icon: "ri-earth-line",
                  desc: "International reach",
                },
              ].map((reach) => (
                <button
                  key={reach.id}
                  type="button"
                  onClick={() =>
                    props.onBusinessReachChange(reach.id as BusinessReach)
                  }
                  className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all cursor-pointer ${props.businessReach === reach.id
                      ? "border-teal-500 bg-teal-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                >
                  <i
                    className={`${reach.icon} text-xl ${props.businessReach === reach.id ? "text-teal-600" : "text-gray-400"}`}
                  ></i>
                  <span
                    className={`text-sm font-medium ${props.businessReach === reach.id ? "text-teal-700" : "text-gray-700"}`}
                  >
                    {reach.label}
                  </span>
                  <span className="text-xs text-gray-400">{reach.desc}</span>
                </button>
              ))}
            </div>
            {props.errors.businessReach && (
              <p className="text-xs text-red-500 mt-2">
                {props.errors.businessReach}
              </p>
            )}
            {/* <p className="text-xs text-gray-400 mt-2">
              Used for keyword classification and GMB routing
            </p> */}
          </div>

          {/* National Options */}
          {props.businessReach === "national" && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
                <span>Coverage Option</span> <span className="text-red-500">*</span>
                <FieldInfo fieldKey="nationalOption" />
              </label>
              <div className="grid grid-cols-2 gap-3">
                {[
                  {
                    id: "panIndia" as NationalOption,
                    label: "Pan India",
                    icon: "ri-flag-line",
                    desc: "All cities / Country-wide",
                  },
                  {
                    id: "cityOrCountry" as NationalOption,
                    label: "City / Country",
                    icon: "ri-building-line",
                    desc: "Select specific locations",
                  },
                ].map((option) => {
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => props.onNationalOptionChange(option.id)}
                      className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all cursor-pointer ${props.nationalOption === option.id
                          ? "border-teal-500 bg-teal-50"
                          : "border-gray-200 hover:border-gray-300 bg-white"
                        }`}
                    >
                      <i
                        className={`${option.icon} text-xl ${props.nationalOption === option.id ? "text-teal-600" : "text-gray-400"}`}
                      ></i>
                      <span
                        className={`text-sm font-medium ${props.nationalOption === option.id ? "text-teal-700" : "text-gray-700"}`}
                      >
                        {option.label}
                      </span>
                      <span
                        className={`text-xs text-gray-400`}
                      >
                        {option.desc}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Pan India Summary */}
          {isPanIndiaSelected && (
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
              <div className="flex items-center gap-2 text-teal-700">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-checkbox-circle-line text-teal-600"></i>
                </div>
                <span className="text-sm font-medium">
                  Pan India selected — coverage applies to all cities
                  country-wide.
                </span>
              </div>
            </div>
          )}

          {/* City / Another Country Input */}
          {isCityOrCountrySelected && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <span>Add Cities / Countries</span> <span className="text-red-500">*</span>
                <FieldInfo fieldKey="nationalCities" />
              </label>
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    ref={nationalInputRef}
                    type="text"
                    value={props.nationalCityInput}
                    onChange={(e) => {
                      props.onNationalCityInputChange(e.target.value);
                      props.onShowNationalDropdown(e.target.value.length > 0);
                    }}
                    placeholder="Start typing a city or country..."
                    className="flex-1 py-2.5 border-b border-gray-200 text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                {props.showNationalDropdown &&
                  props.nationalCityInput.trim() && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                      {nationalSuggestions.map((suggestion) => (
                        <button
                          key={suggestion.place_id}
                          type="button"
                          onClick={() => props.onAddNationalCity(suggestion.description.split(',')[0].trim())}
                          className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer text-gray-700"
                        >
                          <div className="flex items-center gap-2">
                            <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                            {suggestion.description.split(',')[0].trim()}
                          </div>
                        </button>
                      ))}
                      {suggestionError && (
                        <div className="px-4 py-2 text-xs text-red-500">
                          Error: {suggestionError}
                        </div>
                      )}
                      {nationalSuggestions.length === 0 && !suggestionError && (
                        <button
                          type="button"
                          onClick={() =>
                            props.onAddNationalCity(props.nationalCityInput)
                          }
                          className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer text-gray-700 flex items-center gap-2"
                        >
                          <i className="ri-add-line text-teal-500 text-sm"></i>
                          Add "{props.nationalCityInput}"
                        </button>
                      )}
                    </div>
                  )}
              </div>

              {props.nationalCityInput.trim() &&
                !props.showNationalDropdown && (
                  <button
                    type="button"
                    onClick={() =>
                      props.onAddNationalCity(props.nationalCityInput)
                    }
                    className="mt-2 text-xs text-teal-600 hover:text-teal-700 cursor-pointer flex items-center gap-1 font-medium"
                  >
                    <i className="ri-add-line"></i>
                    Add "{props.nationalCityInput}"
                  </button>
                )}

              {props.nationalCities.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {props.nationalCities.map((city, index) => (
                    <div
                      key={`${city}-${index}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700"
                    >
                      <i className="ri-map-pin-line text-teal-500 text-xs"></i>
                      {city}
                      <button
                        type="button"
                        onClick={() => props.onRemoveNationalCity(index)}
                        className="ml-1 text-teal-400 hover:text-teal-600 cursor-pointer w-4 h-4 flex items-center justify-center"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <p className="text-xs text-gray-400 mt-1">
                Autocomplete enabled — type to search or enter custom
                city/country. You can add multiple locations.
              </p>
              {props.errors.nationalCities && (
                <p className="text-xs text-red-500 mt-2">
                  {props.errors.nationalCities}
                </p>
              )}
            </div>
          )}

          {/* Local Location Input */}
          {props.businessReach === "local" && (
            <div>
              <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-1.5">
                <span>Primary Location</span> <span className="text-red-500">*</span>
                <FieldInfo fieldKey="localLocation" />
              </label>
              <div className="relative">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-search-line text-gray-400 text-base"></i>
                  </div>
                  <input
                    ref={localInputRef}
                    type="text"
                    value={props.localLocationInput}
                    onChange={(e) => {
                      props.onLocalLocationInputChange(e.target.value);
                      props.onShowLocalDropdown(e.target.value.length > 0);
                    }}
                    placeholder="Start typing your area..."
                    className="flex-1 py-2.5 border-b border-gray-200 text-sm focus:outline-none focus:border-teal-500"
                  />
                </div>
                {props.showLocalDropdown && props.localLocationInput.trim() && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 max-h-48 overflow-y-auto">
                    {localSuggestions.map((suggestion) => (
                      <button
                        key={suggestion.place_id}
                        type="button"
                        onClick={() => props.onAddLocalLocation(suggestion.description)}
                        className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer text-gray-700"
                      >
                        <div className="flex items-center gap-2">
                          <i className="ri-map-pin-line text-gray-400 text-sm"></i>
                          {suggestion.description}
                        </div>
                      </button>
                    ))}
                    {suggestionError && (
                      <div className="px-4 py-2 text-xs text-red-500">
                        Error: {suggestionError}
                      </div>
                    )}
                    {localSuggestions.length === 0 && !suggestionError && (
                      <button
                        type="button"
                        onClick={() =>
                          props.onAddLocalLocation(props.localLocationInput)
                        }
                        className="w-full px-4 py-2.5 text-sm text-left hover:bg-gray-50 cursor-pointer text-gray-700 flex items-center gap-2"
                      >
                        <i className="ri-add-line text-teal-500 text-sm"></i>
                        Add "{props.localLocationInput}"
                      </button>
                    )}
                  </div>
                )}
              </div>

              {props.localLocationInput.trim() && !props.showLocalDropdown && (
                <button
                  type="button"
                  onClick={() =>
                    props.onAddLocalLocation(props.localLocationInput)
                  }
                  className="mt-2 text-xs text-teal-600 hover:text-teal-700 cursor-pointer flex items-center gap-1 font-medium"
                >
                  <i className="ri-add-line"></i>
                  Add "{props.localLocationInput}"
                </button>
              )}

              {props.localLocations.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {props.localLocations.map((loc, index) => (
                    <div
                      key={`${loc}-${index}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-sm text-teal-700"
                    >
                      <i className="ri-map-pin-line text-teal-500 text-xs"></i>
                      {loc}
                      <button
                        type="button"
                        onClick={() => props.onRemoveLocalLocation(index)}
                        className="ml-1 text-teal-400 hover:text-teal-600 cursor-pointer w-4 h-4 flex items-center justify-center"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* <p className="text-xs text-gray-400 mt-1">
                Autocomplete enabled — type to search or enter custom location.
                You can add multiple areas.
              </p> */}
              {props.errors.localLocations && (
                <p className="text-xs text-red-500 mt-2">
                  {props.errors.localLocations}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Audience Block */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-team-line text-teal-600 text-sm"></i>
          </div>
          Audience
        </h3>

        <div className="space-y-6">
          {/* Audience Type */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <span>Audience Type</span> <span className="text-red-500">*</span>
              <FieldInfo fieldKey="audienceType" />
            </label>
            <div className="grid grid-cols-3 gap-3">
              {[
                {
                  id: "B2B",
                  label: "B2B",
                  icon: "ri-building-4-line",
                  desc: "Business clients",
                },
                {
                  id: "B2C",
                  label: "B2C",
                  icon: "ri-user-line",
                  desc: "Individual consumers",
                },
                {
                  id: "both",
                  label: "Both",
                  icon: "ri-exchange-line",
                  desc: "B2B + B2C",
                },
              ].map((type) => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() =>
                    props.onAudienceTypeChange(type.id as AudienceType)
                  }
                  className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all cursor-pointer ${props.audienceType === type.id
                      ? "border-teal-500 bg-teal-50"
                      : "border-gray-200 hover:border-gray-300 bg-white"
                    }`}
                >
                  <i
                    className={`${type.icon} text-xl ${props.audienceType === type.id ? "text-teal-600" : "text-gray-400"}`}
                  ></i>
                  <span
                    className={`text-sm font-medium ${props.audienceType === type.id ? "text-teal-700" : "text-gray-700"}`}
                  >
                    {type.label}
                  </span>
                  <span className="text-xs text-gray-400">{type.desc}</span>
                </button>
              ))}
            </div>
            {props.errors.audienceType && (
              <p className="text-xs text-red-500 mt-2">
                {props.errors.audienceType}
              </p>
            )}
          </div>

          {/* Persona Details — NEW SECTION */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <span>Persona Details <span className="text-gray-400 font-normal ml-0.5">(Optional)</span></span>
              <FieldInfo fieldKey="personaDetails" />
            </label>
            {/* <p className="text-xs text-gray-500 mb-3">
              Tell us more about your ideal customer so we can tailor the
              strategy.
            </p> */}

            <div className="space-y-4 p-4 bg-white border border-gray-200 rounded-lg">
              {/* Age Range */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-2">
                  <span>Age Range</span>
                  <FieldInfo fieldKey="ageRange" />
                </label>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      { id: "18-24", label: "18-24" },
                      { id: "25-34", label: "25-34" },
                      { id: "35-44", label: "35-44" },
                      { id: "45-54", label: "45-54" },
                      { id: "55+", label: "55+" },
                      { id: "all-ages", label: "All Ages" },
                    ] as { id: AgeRange; label: string }[]
                  ).map((age) => (
                    <button
                      key={age.id}
                      type="button"
                      onClick={() => handleAgeToggle(age.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${(props.targetAgeRange || []).includes(age.id)
                          ? "bg-teal-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                      {age.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Gender */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-2">
                  <span>Gender</span>
                  <FieldInfo fieldKey="gender" />
                </label>
                <div className="flex flex-wrap gap-2">
                  {(
                    [
                      { id: "all", label: "All" },
                      { id: "male", label: "Male" },
                      { id: "female", label: "Female" },
                      { id: "other", label: "Other / Non-binary" },
                    ] as { id: Gender; label: string }[]
                  ).map((g) => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => handleGenderToggle(g.id)}
                      className={`px-3 py-1.5 rounded-full text-xs font-medium transition-all cursor-pointer whitespace-nowrap ${(props.targetGender || []).includes(g.id)
                          ? "bg-teal-500 text-white"
                          : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                      {g.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Interests */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-2">
                  <span>Interests</span>
                  <FieldInfo fieldKey="interests" />
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {props.interests.map((interest, i) => (
                    <div
                      key={`${interest}-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs text-teal-700"
                    >
                      {interest}
                      <button
                        type="button"
                        onClick={() => props.onRemoveInterest(i)}
                        className="ml-1 text-teal-400 hover:text-teal-600 cursor-pointer w-4 h-4 flex items-center justify-center"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={props.interestInput}
                    onChange={(e) =>
                      props.onInterestInputChange(e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        props.onAddInterest(props.interestInput);
                      }
                    }}
                    placeholder="e.g., Fitness, Travel, Technology..."
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => props.onAddInterest(props.interestInput)}
                    className="px-3 py-2 bg-teal-500 text-white rounded-md text-sm hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
              </div>

              {/* Profession */}
              <div>
                <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 mb-2">
                  <span>Profession</span>
                  <FieldInfo fieldKey="profession" />
                </label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {props.professions.map((profession, i) => (
                    <div
                      key={`${profession}-${i}`}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 border border-teal-200 rounded-full text-xs text-teal-700"
                    >
                      {profession}
                      <button
                        type="button"
                        onClick={() => props.onRemoveProfession(i)}
                        className="ml-1 text-teal-400 hover:text-teal-600 cursor-pointer w-4 h-4 flex items-center justify-center"
                      >
                        <i className="ri-close-line text-xs"></i>
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    value={props.professionInput}
                    onChange={(e) =>
                      props.onProfessionInputChange(e.target.value)
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        props.onAddProfession(props.professionInput);
                      }
                    }}
                    placeholder="e.g., Doctor, Designer, Entrepreneur..."
                    className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                  <button
                    type="button"
                    onClick={() => props.onAddProfession(props.professionInput)}
                    className="px-3 py-2 bg-teal-500 text-white rounded-md text-sm hover:bg-teal-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Persona Description */}
          <div>
            <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
              <span>Persona Description <span className="text-gray-400 font-normal ml-0.5">(Optional)</span></span>
              <FieldInfo fieldKey="personaDescription" />
            </label>
            {/* <p className="text-xs text-gray-500 mb-2">
              Describe your typical customer — what they care about and why they
              choose you.
            </p> */}
            <textarea
              value={props.personaDescription}
              onChange={(e) =>
                props.onPersonaDescriptionChange(e.target.value.slice(0, 500))
              }
              placeholder="e.g., Health-conscious millennials aged 25-40 who value organic ingredients and sustainable practices..."
              rows={3}
              maxLength={500}
              className="w-full px-4 py-3 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
            />
            <div className="flex justify-end mt-1">
              <span className="text-xs text-gray-400">
                {props.personaDescription.length}/500
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Physical Presence Block */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-store-line text-teal-600 text-sm"></i>
          </div>
          Physical Presence
        </h3>

        <div className="space-y-3">
          <label className="flex items-center gap-1.5 text-sm font-medium text-gray-700 mb-2">
            <span>Do customers visit your physical location?</span> <span className="text-red-500">*</span>
            <FieldInfo fieldKey="physicalPresence" />
          </label>
          <div className="grid grid-cols-3 gap-3">
            {[
              {
                id: "yes",
                label: "Yes",
                desc: "Walk-in customers",
                icon: "ri-door-open-line",
              },
              {
                id: "no",
                label: "No",
                desc: "Fully online / remote",
                icon: "ri-wifi-line",
              },
              {
                id: "both",
                label: "Both",
                desc: "Online + in-person",
                icon: "ri-links-line",
              },
            ].map((presence) => (
              <button
                key={presence.id}
                type="button"
                onClick={() =>
                  props.onPhysicalPresenceChange(
                    presence.id as PhysicalPresence,
                  )
                }
                className={`flex flex-col items-center gap-2 p-4 border-2 rounded-lg transition-all cursor-pointer ${props.physicalPresence === presence.id
                    ? "border-teal-500 bg-teal-50"
                    : "border-gray-200 hover:border-gray-300 bg-white"
                  }`}
              >
                <i
                  className={`${presence.icon} text-xl ${props.physicalPresence === presence.id ? "text-teal-600" : "text-gray-400"}`}
                ></i>
                <span
                  className={`text-sm font-medium ${props.physicalPresence === presence.id ? "text-teal-700" : "text-gray-700"}`}
                >
                  {presence.label}
                </span>
                <span className="text-xs text-gray-400">{presence.desc}</span>
              </button>
            ))}
          </div>
          {props.errors.physicalPresence && (
            <p className="text-xs text-red-500 mt-2">
              {props.errors.physicalPresence}
            </p>
          )}
          {/* <p className="text-xs text-gray-400 mt-1">
            Drives GMB routing logic and local SEO strategy
          </p> */}
        </div>
      </div>

      <div className="border-t border-gray-100"></div>

      {/* Competitor Identification Block */}
      <div>
        <h3 className="text-sm font-semibold text-gray-900 mb-4 flex items-center gap-2">
          <div className="w-7 h-7 rounded-md bg-teal-500/10 flex items-center justify-center">
            <i className="ri-crosshair-line text-teal-600 text-sm"></i>
          </div>
          <span>Competitor Identification</span>
          <FieldInfo fieldKey="competitors" />
        </h3>

        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            {/* Add up to 5 competitors you want to benchmark against. Include their
            brand name, website, and any strategic insights you have. */}
            <span className="block font-medium text-teal-600">
              Note: You must add at least one competitor, and for each competitor, both brand name and website URL are required.
            </span>
          </p>

          <div className="space-y-4">
            {props.competitors
              .slice(0, props.visibleCompetitorCount)
              .map((competitor, index) => (
                <div
                  key={index}
                  className="bg-white border border-gray-200 rounded-lg p-4 space-y-3"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-sm font-semibold text-orange-600">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1">
                      <input
                        type="text"
                        value={competitor.name}
                        onChange={(e) =>
                          props.onUpdateCompetitor(index, "name", e.target.value)
                        }
                        placeholder="Competitor brand name"
                        className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${props.errors[`competitors_${index}_name`] ? "border-red-300" : "border-gray-200"}`}
                      />
                      {props.errors[`competitors_${index}_name`] && (
                        <p className="text-xs text-red-500 mt-1">
                          {props.errors[`competitors_${index}_name`]}
                        </p>
                      )}
                    </div>
                    {(competitor.name ||
                      competitor.website ||
                      competitor.insights) && (
                        <button
                          type="button"
                          onClick={() => props.onRemoveCompetitor(index)}
                          className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      )}
                  </div>
                  <div>
                    <input
                      type="url"
                      value={competitor.website}
                      onChange={(e) =>
                        props.onUpdateCompetitor(index, "website", e.target.value)
                      }
                      placeholder="https://competitor-website.com"
                      className={`w-full px-4 py-2.5 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent ${props.errors[`competitors_${index}_website`] ? "border-red-300" : "border-gray-200"}`}
                    />
                    {props.errors[`competitors_${index}_website`] && (
                      <p className="text-xs text-red-500 mt-1">
                        {props.errors[`competitors_${index}_website`]}
                      </p>
                    )}
                  </div>
                  <textarea
                    value={competitor.insights}
                    onChange={(e) =>
                      props.onUpdateCompetitor(
                        index,
                        "insights",
                        e.target.value,
                      )
                    }
                    placeholder="Competition insights — what they do well, their positioning, or any notes..."
                    rows={2}
                    maxLength={300}
                    className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none"
                  />
                </div>
              ))}
            {props.errors.competitors && (
              <p className="text-xs text-red-500 mt-2">
                {props.errors.competitors}
              </p>
            )}

            {props.errors.apiError && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600 text-sm">
                <i className="ri-error-warning-line"></i>
                {props.errors.apiError}
              </div>
            )}

            {props.visibleCompetitorCount < props.competitors.length && (
              <button
                type="button"
                onClick={props.onShowMoreCompetitors}
                className="w-full py-3 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:text-teal-600 hover:border-teal-300 transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                <i className="ri-add-line"></i>
                Add more competitors
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
